import { useContext, useEffect, useState } from 'react';
import { AppContext } from 'contexts/AppContext';
import { useSessionsData } from 'hooks/useSessionsData';
import { exportCsv } from 'lib/exportCsv';
import { formatUtcDateTime } from 'utils/helperFunctions';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';
import {
  ArrowDown, ArrowUp, ChevronLeftIcon, ChevronRightIcon, MessageSquare, Search, Smartphone,
  Monitor, HelpCircle, ThumbsDown, ThumbsUp, Wrench, X, AlertTriangle, Mic, Download } from 'lucide-react';

/**
 * Sessions — read one conversation.
 *
 * The Messages tab is a flat table, one row per message, all users interleaved:
 * reading a single exchange means finding its rows by eye and mentally putting
 * them back in order. This tab is the other half — a list of conversations on
 * the left, the full transcript of the selected one on the right.
 */

const PLATFORM_META = {
  mobile:  { label: 'Mobile',  Icon: Smartphone },
  web:     { label: 'Web',     Icon: Monitor },
  unknown: { label: 'Unknown', Icon: HelpCircle },
};

/**
 * `device` carries two different kinds of value and the backend derives
 * platform from it (mobile/web/unknown). It deliberately never reports
 * "desktop": a User-Agent tells you form factor, not platform, and defaulting
 * unknown values to desktop is the existing bug in the /send/get-messages path.
 * Anything outside the three known values is rendered as-is rather than
 * silently bucketed, so a backend change shows up here instead of hiding.
 */
function PlatformBadge({ platform }) {
  const meta = PLATFORM_META[platform];
  if (!meta) return <Badge variant="outline">{platform || '—'}</Badge>;
  const { label, Icon } = meta;
  return (
    <Badge variant="outline" className="gap-1 font-normal">
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

// Timestamps render in UTC, the zone the date filter uses -- see helperFunctions.
const fmtTime = (v) => formatUtcDateTime(v);

const fmtSeconds = (v) =>
  v === null || v === undefined || v === '' ? '—' : `${Number(v).toFixed(2)}s`;

/** metadata.function_calls is what the agent actually invoked for this turn. */
function toolNames(metadata) {
  const calls = metadata?.function_calls;
  if (!Array.isArray(calls)) return [];
  return calls
    .map((c) => c?.name || c?.function || (typeof c === 'string' ? c : null))
    .filter(Boolean);
}

function Turn({ turn }) {
  const tools = toolNames(turn.metadata);
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-3">
        <span>{fmtTime(turn.time_stamp)}</span>
        <span>{fmtSeconds(turn.response_time)}</span>
        <PlatformBadge platform={turn.platform} />
        {turn.chat_language && <span className="uppercase">{turn.chat_language}</span>}
        {turn.query_category && <Badge variant="secondary" className="font-normal">{turn.query_category}</Badge>}
        {/* HANDS-FREE AND DICTATION ARE DIFFERENT THINGS and used to render the
            same word. This badge read `is_voice_input` alone, and adk-v2 #94
            began setting that true for hands-free turns as well -- so a spoken
            conversation and a message typed by voice into the chat box became
            indistinguishable.
            The discriminator is the one the voice bridge documents:
              hands-free  ->  input_source = 'voice'
              dictation   ->  is_voice_input AND input_source <> 'voice'
            Keyed on is_voice_input rather than on original_transcription being
            present: 6 of the 30 dictated turns in 90 days carry no transcript,
            and those are dictated messages too. */}
        {turn.metadata?.input_source === 'voice' ? (
          <Badge variant="secondary" className="font-normal">Voice</Badge>
        ) : turn.is_voice_input ? (
          <Badge variant="secondary" className="font-normal">Dictated</Badge>
        ) : null}
        {turn.feedback === 1 && (
          <span className="inline-flex items-center gap-1 text-emerald-600"><ThumbsUp className="h-3 w-3" />Positive</span>
        )}
        {turn.feedback === -1 && (
          <span className="inline-flex items-center gap-1 text-red-600"><ThumbsDown className="h-3 w-3" />Negative</span>
        )}
      </div>

      {/* What the user actually SAID, shown only when it differs from what they
          sent. Dictation lands in an editable box, so a correction here is the
          only visible evidence of a transcription error -- and showing it when
          the strings match would just be the message twice. */}
      {turn.original_transcription
        && turn.original_transcription.trim() !== (turn.input || '').trim() && (
        <div className="mb-3 rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">
            Heard as
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 italic">
            {turn.original_transcription}
          </div>
        </div>
      )}

      <div className="font-medium whitespace-pre-wrap mb-2">{turn.input || <span className="italic text-gray-400">(no input recorded)</span>}</div>
      <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200">{turn.output || <span className="italic text-gray-400">(no output recorded)</span>}</div>

      {turn.feedback_message && (
        <div className="mt-3 text-sm border-l-2 border-zinc-300 pl-3 text-gray-600">
          <span className="font-medium">User said: </span>{turn.feedback_message}
        </div>
      )}
      {turn.correct_answer && (
        <div className="mt-2 text-sm border-l-2 border-emerald-400 pl-3 text-gray-600">
          <span className="font-medium">Expected: </span>{turn.correct_answer}
        </div>
      )}
      {turn.errors && (
        <div className="mt-2 text-sm text-red-600 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="whitespace-pre-wrap">{turn.errors}</span>
        </div>
      )}
      {tools.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1 text-xs text-gray-500">
          <Wrench className="h-3 w-3" />
          {tools.map((t, i) => <Badge key={`${t}-${i}`} variant="outline" className="font-normal">{t}</Badge>)}
        </div>
      )}
    </div>
  );
}

export default function SessionsTab() {
  const {
    selectedClient, dateRange, sessionsPrefill, setSessionsPrefill,
  } = useContext(AppContext);
  const {
    sessions, loading, error, page, hasMore, pageSize, fetchSessions,
    thread, threadTotal, threadLoading, threadError, activeSession, fetchThread, clearThread,
  } = useSessionsData();

  // Seeded from the one-shot prefill, not '': this tab unmounts whenever
  // another tab is shown, so a click-through always MOUNTS it fresh — and the
  // first fetch effect fires before the consuming effect below can setState.
  // Seeding at init makes the first request already carry the filter; without
  // it the unfiltered mount fetch races the filtered one, and whichever
  // response lands last owns the list (observed with the category drill,
  // 2026-08-27).
  const [feedback, setFeedback] = useState(() => sessionsPrefill?.feedback || '');
  // The audience control this tab never had -- each row carries a badge, but
  // a badge cannot EXCLUDE. Same three states as every other tab; '' = all,
  // matching this tab's toggle-off convention for its other filter buttons.
  const [audience, setAudience] = useState('');
  const excludeInternal = audience === 'customers';
  const internalOnly = audience === 'internal';
  const [platform, setPlatform] = useState('');
  const [modality, setModality] = useState('');
  // Which conversations, on what topic. Set only by another tab's click-through
  // (the Quality categories card) — this tab renders it as a removable chip
  // rather than growing a 12-button category picker of its own. Seeded at init
  // for the same race reason as `feedback` above.
  const [category, setCategory] = useState(() => sessionsPrefill?.category || '');
  // false = newest first, the list's historical order and the endpoint default.
  const [sortAsc, setSortAsc] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [appliedUser, setAppliedUser] = useState('');

  // The prefill's values are consumed by the state initializers above; this
  // only clears it so back-and-forth navigation doesn't re-apply a stale
  // filter. The setState calls cover the (currently impossible) case of a
  // prefill arriving while this tab is already mounted — for the mount case
  // they re-set the seeded values, which React bails out of without a render.
  useEffect(() => {
    if (!sessionsPrefill) return;
    if (sessionsPrefill.feedback) setFeedback(sessionsPrefill.feedback);
    if (sessionsPrefill.category) setCategory(sessionsPrefill.category);
    setSessionsPrefill(null);
  }, [sessionsPrefill, setSessionsPrefill]);

  const sort = sortAsc ? 'asc' : 'desc';

  // fetchSessions and clearThread are useCallback'd on stable inputs, so listing
  // them here is honest rather than loop-inducing -- no eslint suppression needed.
  useEffect(() => {
    fetchSessions({ feedback, platform, userId: appliedUser, pageIndex: 0,
                    excludeInternal, internalOnly, modality, category, sort });
    clearThread();
  }, [selectedClient, dateRange, feedback, platform, appliedUser,
      audience, excludeInternal, internalOnly, modality, category, sort,
      fetchSessions, clearThread]);

  const go = (pageIndex) => {
    fetchSessions({ feedback, platform, userId: appliedUser, pageIndex,
                    excludeInternal, internalOnly, modality, category, sort });
    clearThread();
  };

  const filterBtn = (value, current, setter, label) => (
    <Button
      key={label}
      size="sm"
      variant={current === value ? 'default' : 'outline'}
      onClick={() => setter(current === value ? '' : value)}
    >
      {label}
    </Button>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-6">
      {/* ── Conversation list ─────────────────────────────────────────── */}
      {/* Sticky on wide screens: the transcript beside it can run to hundreds of
          turns, and a list that scrolls away with it makes switching conversations
          a round trip to the top of the page. Falls back to normal flow on narrow
          screens, where the two panes stack. */}
      <Card className="min-w-0 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Conversations</CardTitle>
          <div className="flex flex-wrap gap-2 pt-3">
            {filterBtn('negative', feedback, setFeedback, 'Thumbs Down')}
            {filterBtn('positive', feedback, setFeedback, 'Thumbs Up')}
            {filterBtn('mobile', platform, setPlatform, 'Mobile')}
            {filterBtn('web', platform, setPlatform, 'Web')}
            {filterBtn('voice', modality, setModality, 'Voice')}
            {filterBtn('text', modality, setModality, 'Text')}
            <span className="mx-1 h-4 w-px bg-zinc-300 dark:bg-zinc-700" />
            {filterBtn('customers', audience, setAudience, 'Customers')}
            {filterBtn('internal', audience, setAudience, 'Internal')}
            {/* Order toggle, not a filter: the list stays the same, so it never
                highlights as "engaged" the way the buttons above do. */}
            <Button size="sm" variant="outline" onClick={() => setSortAsc(!sortAsc)}>
              {sortAsc
                ? <ArrowUp className="h-3.5 w-3.5 mr-1" />
                : <ArrowDown className="h-3.5 w-3.5 mr-1" />}
              {sortAsc ? 'Oldest first' : 'Newest first'}
            </Button>
            {sessions.length > 0 && (
              <Button size="sm" variant="outline" className="ml-auto"
                      onClick={() => exportCsv('sessions', sessions)}>
                <Download className="h-3.5 w-3.5 mr-1" />CSV
              </Button>
            )}
          </div>
          {/* The topic chip a categories-card click arrives with. Removable
              here; there is deliberately no picker — the full category list
              lives on Quality, where each row shows its volume. */}
          {category && (
            <div className="pt-2">
              <Button size="sm" variant="secondary" onClick={() => setCategory('')}>
                <span className="capitalize">
                  {category === '(uncategorised)'
                    ? 'Not categorised'
                    : category.replace(/_/g, ' ')}
                </span>
                <X className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
          )}
          <form
            className="flex gap-2 pt-2"
            onSubmit={(e) => { e.preventDefault(); setAppliedUser(userQuery.trim()); }}
          >
            <Input
              placeholder="Look up a user by email…"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              className="h-9"
            />
            <Button type="submit" size="sm" variant="outline" aria-label="Search User">
              <Search className="h-4 w-4" />
            </Button>
            {appliedUser && (
              <Button
                type="button" size="sm" variant="ghost"
                onClick={() => { setUserQuery(''); setAppliedUser(''); }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </form>
        </CardHeader>

        <CardContent className="px-0">
          {error && <div className="px-6 py-4 text-sm text-red-600">{error}</div>}
          {loading && <div className="px-6 py-8 text-sm text-gray-500">Loading conversations…</div>}

          {!loading && !error && sessions.length === 0 && (
            <div className="px-6 py-8 text-sm text-gray-500">
              No conversations match these filters in the selected date range.
            </div>
          )}

          {!loading && sessions.length > 0 && (
            /* A stacked list, not a table: this pane is the narrow half of a
               master-detail split, and five columns forced a horizontal scroll
               to reach Platform and Feedback. Two lines per row always fit. */
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {sessions.map((s) => {
                const selected = activeSession?.session_id === s.session_id
                  && activeSession?.user_id === s.user_id;
                return (
                  <li key={`${s.user_id}-${s.session_id}`}>
                    <button
                      type="button"
                      onClick={() => fetchThread(s)}
                      className={`w-full text-left px-6 py-3 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900 ${
                        selected ? 'bg-zinc-100 dark:bg-zinc-800' : ''
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="truncate text-sm font-medium" title={s.user_id}>
                          {s.user_id || '—'}
                        </span>
                        <span className="shrink-0 text-xs text-gray-500">
                          {fmtTime(s.last_activity_at)}
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                        <PlatformBadge platform={s.platform} />
                        {/* Voice conversations live in these same rows and were
                            readable here all along -- the transcript is the
                            user's speech and the model's own output
                            transcription -- but nothing marked them, so the only
                            tell was that the session id happens to look
                            different. Rendered only when true, so text sessions
                            are unchanged. */}
                        {s.is_voice && (
                          <span className="inline-flex items-center gap-1 text-violet-700 dark:text-violet-400">
                            <Mic className="h-3 w-3" />Voice
                          </span>
                        )}
                        {/* Only when the label is present and not prod: with the
                            default Prod view these rows are filtered out anyway,
                            and an unlabeled row gets no badge rather than a
                            guessed one. */}
                        {s.environment && s.environment !== 'prod' && (
                          <span className="uppercase text-amber-700 dark:text-amber-400">
                            {s.environment}
                          </span>
                        )}
                        <span>{s.turns} turn{s.turns === 1 ? '' : 's'}</span>
                        {s.account_type && <span>{s.account_type}</span>}
                        {s.thumbs_up > 0 && (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <ThumbsUp className="h-3 w-3" />{s.thumbs_up}
                          </span>
                        )}
                        {s.thumbs_down > 0 && (
                          <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                            <ThumbsDown className="h-3 w-3" />{s.thumbs_down}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex items-center justify-between px-6 pt-4">
            <span className="text-xs text-gray-500">
              {sessions.length > 0
                ? `Showing ${page * pageSize + 1}–${page * pageSize + sessions.length}`
                : ''}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0 || loading} onClick={() => go(page - 1)}>
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" disabled={!hasMore || loading} onClick={() => go(page + 1)}>
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Transcript ────────────────────────────────────────────────── */}
      <Card className="min-w-0">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            {activeSession ? 'Conversation' : 'Select a conversation'}
          </CardTitle>
          {activeSession && (
            <div className="text-xs text-gray-500 pt-1 space-y-0.5">
              <div className="truncate" title={activeSession.user_id}>{activeSession.user_id}</div>
              <div>
                {threadLoading
                  ? 'Loading…'
                  : `${thread?.length || 0}${
                      threadTotal > (thread?.length || 0) ? ` of ${threadTotal}` : ''
                    } turn${threadTotal === 1 ? '' : 's'} · started ${fmtTime(activeSession.started_at)}`}
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent>
          {!activeSession && (
            <p className="text-sm text-gray-500 py-8">
              Pick a conversation on the left to read it end to end.
            </p>
          )}

          {threadError && <div className="text-sm text-red-600 py-4">{threadError}</div>}
          {threadLoading && <div className="text-sm text-gray-500 py-8">Loading conversation…</div>}

          {/* The thread endpoint returns the conversation's FULL turn count, not
              the page's. Surfacing it is the point: the longest real conversation
              measured is 1,094 turns, so without this a truncated page looks
              exactly like a complete one. */}
          {!threadLoading && thread && threadTotal > thread.length && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Showing the first {thread.length} of {threadTotal} turns. This conversation is
                longer than one page.
              </span>
            </div>
          )}

          {!threadLoading && thread && thread.length === 0 && !threadError && (
            <p className="text-sm text-gray-500 py-8">This conversation has no turns in range.</p>
          )}

          {!threadLoading && thread && thread.length > 0 && (
            <div className="space-y-3">
              {thread.map((t, i) => <Turn key={t.message_id || i} turn={t} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
